// QW-PHOTO-REOPEN-FIX (2026-06-17): preserve a saved quote's photo assignment
// when it is re-opened from Quote History.
//
// The photo-auto-assign watcher in App.jsx re-runs the (non-deterministic) AI
// photo-to-scope assignment whenever a photo id is absent from its
// `lastAssignedPhotoIdsRef` dedup set. On project load that set was reset to
// empty, so every saved photo looked "new" and the watcher re-assigned them,
// changing scope[].images + the hero vs what was saved.
//
// This helper decides what to seed that set with on load:
//   - If the loaded scope already carries images, the project was assigned before:
//     seed the set with its photo ids so the watcher leaves the assignment alone.
//   - If the scope has no images yet (an interrupted / unassigned draft): return an
//     empty set so the first real auto-assign can still run once scope + photos are
//     both present. Otherwise we would permanently suppress assignment on that draft.
//
// derivePhotoId MUST mirror the id derivation used by the watcher effect in
// App.jsx (p.id || p.blobId || p.s3Key || p.thumbnailUrl || p.name) so the seeded
// ids match what the watcher checks.
interface PhotoLike {
    id?: unknown;
    blobId?: unknown;
    s3Key?: unknown;
    fileMetadataId?: unknown;
    thumbnailUrl?: unknown;
    name?: unknown;
}

function durablePhotoReviewKey(photo: PhotoLike | null | undefined): string | null {
    if (!photo) return null;
    const candidates: Array<[string, unknown]> = [
        ['id', photo.id],
        ['blob', photo.blobId],
        ['s3', photo.s3Key],
        ['file', photo.fileMetadataId],
        ['name', photo.name],
    ];
    for (const [kind, raw] of candidates) {
        const value = typeof raw === 'string' ? raw.trim() : '';
        if (!value || value.length > 512 || /^(?:data|blob|https?):/i.test(value)) continue;
        return `${kind}:${value}`;
    }
    return null;
}

interface ProjectLike {
    scope?: unknown;
    photos?: unknown;
    quoteData?: unknown;
    selectedHeroImage?: unknown;
    completedStages?: unknown;
}

interface AcceptedZeroPhotoReviewReceipt {
    schemaVersion?: unknown;
    status?: unknown;
    photoKeys?: unknown;
    scopeKeys?: unknown;
    acceptedAt?: unknown;
    acceptedBy?: unknown;
}

interface ScopePhotoReviewItem {
    images?: unknown;
    photoAssignmentsEditedBy?: unknown;
    photoAssignmentsLocked?: unknown;
    photoAssignmentReviewReceipt?: AcceptedZeroPhotoReviewReceipt | null;
    [key: string]: unknown;
}

// Returns the first truthy id candidate (any of the 5 fields, in order) or null.
// The runtime value is whatever the matched field holds (typically a string);
// the union preserves the actual `|| null` behaviour without asserting a type.
export function derivePhotoId(p: PhotoLike | null | undefined): unknown {
    return (p && (p.id || p.blobId || p.s3Key || p.thumbnailUrl || p.name)) || null;
}

function scopeArrayHasImages(scope: unknown): boolean {
    return Array.isArray(scope) && scope.some(
        (it) => Array.isArray((it as { images?: unknown })?.images) && (it as { images: unknown[] }).images.length > 0,
    );
}

function scopePhotoReviewTopologyKeys(scope: unknown): string[] {
    if (!Array.isArray(scope)) return [];
    return scope.map((rawItem, index) => {
        const item = rawItem as Record<string, any> | null;
        if (item?.id != null && String(item.id).trim()) return `id:${String(item.id).trim()}`;
        if (item?.activityRef?.activityId != null && String(item.activityRef.activityId).trim()) {
            return `activity:${String(item.activityRef.activityId).trim()}`;
        }
        if (item?.code != null && String(item.code).trim()) return `code:${String(item.code).trim()}`;
        return `index:${index}`;
    });
}

function sameStringArray(left: unknown, right: unknown): boolean {
    return Array.isArray(left)
        && Array.isArray(right)
        && left.length === right.length
        && left.every((value, index) => value === right[index]);
}

function sameScopeTopology(left: unknown, right: unknown): boolean {
    return Array.isArray(left)
        && Array.isArray(right)
        && left.length === right.length
        && left.every((value, index) => (
            value === right[index]
            // Some legacy Stage 5 snapshots retained stable ids while their
            // top-level scope projection retained only positional rows. Equal
            // positions are the strongest comparison available in that case;
            // additions/deletions still invalidate through the length check.
            || String(value).startsWith('index:')
            || String(right[index]).startsWith('index:')
        ));
}

function isAcceptedZeroPhotoReviewReceipt(
    receipt: AcceptedZeroPhotoReviewReceipt | null | undefined,
): receipt is AcceptedZeroPhotoReviewReceipt {
    return !!(
        receipt
        && typeof receipt === 'object'
        && (receipt.schemaVersion === 1 || receipt.schemaVersion === 2)
        && receipt.status === 'all-omitted'
        && Array.isArray(receipt.photoKeys)
        && receipt.photoKeys.every((key) => typeof key === 'string' && key.trim().length > 0)
        && Number.isFinite(Date.parse(String(receipt.acceptedAt || '')))
        && (receipt.acceptedBy === 'builder' || receipt.acceptedBy === 'ai')
    );
}

function sameAcceptedZeroReceipt(
    left: AcceptedZeroPhotoReviewReceipt | null | undefined,
    right: AcceptedZeroPhotoReviewReceipt,
): boolean {
    return isAcceptedZeroPhotoReviewReceipt(left)
        && left.schemaVersion === right.schemaVersion
        && left.acceptedAt === right.acceptedAt
        && left.acceptedBy === right.acceptedBy
        && sameStringArray(left.photoKeys, right.photoKeys)
        && (
            left.schemaVersion !== 2
            || sameStringArray(left.scopeKeys, right.scopeKeys)
        );
}

function receiptOwnsScopeTopology({
    receipt,
    receiptScope,
    currentScope,
}: {
    receipt: AcceptedZeroPhotoReviewReceipt;
    receiptScope: unknown;
    currentScope: unknown;
}): boolean {
    const currentKeys = scopePhotoReviewTopologyKeys(currentScope);
    if (currentKeys.length === 0) return false;
    if (receipt.schemaVersion === 2) {
        return sameScopeTopology(receipt.scopeKeys, currentKeys);
    }

    // v1 receipts pre-date an embedded topology key. They were stamped onto
    // every row, so they remain trustworthy only while every current/source
    // row still carries the same receipt and both topologies are identical.
    // A later row addition, deletion or reorder therefore fails closed without
    // forcing every existing accepted-zero project to spend another AI call.
    if (!Array.isArray(receiptScope)
        || !sameScopeTopology(scopePhotoReviewTopologyKeys(receiptScope), currentKeys)) return false;
    return receiptScope.length > 0 && receiptScope.every((item) => sameAcceptedZeroReceipt(
        (item as ScopePhotoReviewItem | null)?.photoAssignmentReviewReceipt,
        receipt,
    ));
}

function acceptedZeroReceipt(
    scope: unknown,
    currentScope: unknown = scope,
): AcceptedZeroPhotoReviewReceipt | null {
    if (!Array.isArray(scope)) return null;
    const receipts = scope
        .map((item) => (item as ScopePhotoReviewItem | null)?.photoAssignmentReviewReceipt)
        .filter(isAcceptedZeroPhotoReviewReceipt)
        .filter((receipt) => receiptOwnsScopeTopology({ receipt, receiptScope: scope, currentScope }));
    if (receipts.length === 0) return null;
    return receipts.reduce((latest, receipt) => (
        Date.parse(String(receipt.acceptedAt)) > Date.parse(String(latest.acceptedAt))
            ? receipt
            : latest
    ));
}

function acceptedZeroPhotoKeys(project: ProjectLike | null | undefined): Set<string> | null {
    const quoteData = (project?.quoteData ?? null) as { scope?: unknown; scopeItems?: unknown } | null;
    const candidateScopes = [project?.scope, quoteData?.scope, quoteData?.scopeItems];
    const currentScope = candidateScopes.find((scope) => Array.isArray(scope) && scope.length > 0);
    const candidates = candidateScopes
        .map((scope) => acceptedZeroReceipt(scope, currentScope))
        .filter((receipt): receipt is AcceptedZeroPhotoReviewReceipt => !!receipt);
    if (candidates.length === 0) return null;
    const latest = candidates.reduce((current, receipt) => (
        Date.parse(String(receipt.acceptedAt)) > Date.parse(String(current.acceptedAt))
            ? receipt
            : current
    ));
    return new Set((latest.photoKeys as string[]).map((key) => key.trim()));
}

/**
 * Mint the durable builder-acceptance receipt for an intentional zero-link
 * Stage 4 photo review. The exact evidence keys are copied onto every scope
 * item so a later topology merge cannot orphan the receipt. No linked-photo
 * scope can be labelled all-omitted.
 */
export function markAcceptedZeroScopePhotoReview<T extends ScopePhotoReviewItem>(
    scope: T[] = [],
    photos: PhotoLike[] = [],
    acceptedAt: string = new Date().toISOString(),
    acceptedBy: 'builder' | 'ai' = 'builder',
): T[] {
    if (!Array.isArray(scope) || scope.length === 0) return scope;
    if (scopeArrayHasImages(scope)) return scope;
    const photoKeys = [...new Set(
        (Array.isArray(photos) ? photos : [])
            .map(durablePhotoReviewKey)
            .filter((key): key is string => !!key),
    )].sort();
    if (photoKeys.length === 0) return scope;
    const timestamp = Number.isFinite(Date.parse(String(acceptedAt)))
        ? new Date(acceptedAt).toISOString()
        : new Date().toISOString();
    const scopeKeys = scopePhotoReviewTopologyKeys(scope);
    const receipt = Object.freeze({
        schemaVersion: 2,
        status: 'all-omitted',
        photoKeys,
        scopeKeys,
        acceptedAt: timestamp,
        acceptedBy,
    });
    return scope.map((item) => ({
        ...markScopePhotoAssignmentsEdited(
            item,
            acceptedBy === 'ai' ? 'photo-review-ai' : 'photo-review',
            timestamp,
        ),
        photoAssignmentReviewReceipt: receipt,
    })) as T[];
}

/**
 * True only when an accepted all-omitted receipt exists but no longer owns the
 * live scope topology. The attended watcher may spend one fresh assignment in
 * this case even though no new photo was added: the new/reordered scope row was
 * never part of the builder/AI review represented by the saved receipt.
 */
export function hasInvalidatedAcceptedZeroScopePhotoReview(scope: unknown): boolean {
    if (!Array.isArray(scope) || scope.length === 0 || scopeArrayHasImages(scope)) return false;
    const receipts = scope
        .map((item) => (item as ScopePhotoReviewItem | null)?.photoAssignmentReviewReceipt)
        .filter(isAcceptedZeroPhotoReviewReceipt);
    if (receipts.length === 0) return false;
    return !receipts.some((receipt) => receiptOwnsScopeTopology({
        receipt,
        receiptScope: scope,
        currentScope: scope,
    }));
}

function scopeArrayHasBuilderAcceptedZero(scope: unknown): boolean {
    if (!Array.isArray(scope) || scope.length === 0 || scopeArrayHasImages(scope)) return false;
    const carriesStructuredReceipt = scope.some((item) => isAcceptedZeroPhotoReviewReceipt(
        (item as ScopePhotoReviewItem | null)?.photoAssignmentReviewReceipt,
    ));
    if (carriesStructuredReceipt) {
        // A structured receipt that no longer matches this topology is not a
        // legacy manual-selection signal; it is positive evidence that the
        // current topology has never been reviewed.
        return !!acceptedZeroReceipt(scope, scope);
    }
    return scope.some((item) => {
        const candidate = item as ScopePhotoReviewItem | null;
        return candidate?.photoAssignmentsEditedBy === 'user'
            || candidate?.photoAssignmentsEditedBy === 'photo-review'
            || candidate?.photoAssignmentsLocked === true;
    });
}

// QW-PHOTO-REOPEN-FIX widened (2026-06-24): the original guard only treated a
// project as "already assigned" when project.scope[].images was populated. A
// completed quote's photo-bearing scope can instead live in quoteData.scope, the
// hero can be the only saved assignment. Those durable media links count as a
// processed receipt. Stage completion alone does not: a project can reach Stage
// 3/4 before the old background watcher ever attaches one photo. In that case
// the watcher must retry on reopen instead of permanently suppressing recovery.
// For durable image/hero signals the seed prevents the
// non-deterministic AI from reassigning them on reopen (the "different photos on
// re-open" bug). Either durable signal counts as "already processed", so the
// saved photo arrangement is left exactly as it was saved.
export function seenPhotoIdsForLoadedProject(project: ProjectLike | null | undefined): Set<unknown> {
    const photos: PhotoLike[] = Array.isArray(project?.photos) ? project.photos : [];
    if (photos.length === 0) return new Set();
    const quoteData = (project?.quoteData ?? null) as { scope?: unknown; scopeItems?: unknown } | null;
    const wasProcessed =
        scopeArrayHasImages(project?.scope)
        || scopeArrayHasImages(quoteData?.scope)
        || scopeArrayHasImages(quoteData?.scopeItems)
        || !!project?.selectedHeroImage;
    if (!wasProcessed) {
        const reviewedPhotoKeys = acceptedZeroPhotoKeys(project);
        if (reviewedPhotoKeys) {
            // Seed only evidence covered by the saved receipt. Removed photos
            // do not re-fire; a newly-added identity remains unseen and
            // receives one normal watcher attempt. Explicit Retry bypasses
            // this set entirely.
            return new Set(photos
                .filter((photo) => {
                    const key = durablePhotoReviewKey(photo);
                    return !!key && reviewedPhotoKeys.has(key);
                })
                .map(derivePhotoId)
                .filter(Boolean));
        }
        // Legacy Stage 4/5 user edits already persisted an explicit authority
        // stamp but pre-date the exact evidence receipt. Honour an intentional
        // all-empty builder selection; new versions replace this conservative
        // fallback with the exact-key receipt above on the next acceptance.
        // Only the currently authoritative topology may exercise the legacy
        // stamp-only fallback. A stale quoteData scope must not hide a new live
        // Stage 4 row merely because the old quote snapshot still carries the
        // original photo-review stamp.
        const currentScope = [project?.scope, quoteData?.scope, quoteData?.scopeItems]
            .find((candidate) => Array.isArray(candidate) && candidate.length > 0);
        if (scopeArrayHasBuilderAcceptedZero(currentScope)) {
            return new Set(photos.map(derivePhotoId).filter(Boolean));
        }
        return new Set();
    }
    return new Set(photos.map(derivePhotoId).filter(Boolean));
}
import { markScopePhotoAssignmentsEdited } from './scope-photo-authority';
